import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import geojson, {MultiPolygon, Polygon} from 'geojson';
import I from 'immutable';
import moment from 'moment';
import React from 'react';

import DateSelectCard, {
  HiddenImageAction,
  RemoveFromCartAction,
} from 'app/components/DateSelectCard';
import {SceneStatusBadge, SceneStatusOverlay, sceneStatus} from 'app/components/SceneStatus';
import {isCartEnabled, useCart} from 'app/modules/Cart';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiImageryContract} from 'app/modules/Remote/Project';
import {OrderableScenesLoader} from 'app/providers/OrderableScenesProvider';
import {FEATURE_WAITING_FOR_PROCESSING_STATUS, SOURCE_DETAILS_BY_ID} from 'app/utils/constants';
import * as C from 'app/utils/constants';
import * as featureUtils from 'app/utils/featureUtils';
import {getIntersectionInAcres, intersectGeometries} from 'app/utils/geoJsonUtils';
import {CachedApiGetterOptions, StatusMaybe} from 'app/utils/hookUtils';
import {
  canLoadOrderableSceneOnMap,
  getLayerKeyForScene,
  orderableScenePairKey,
} from 'app/utils/imageryUtils';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {getOrgPrefix, paysAtCheckout} from 'app/utils/organizationUtils';

import PolygonIcon from '../PolygonIcon';
import cs from './styles.module.scss';
import {isSceneInTab} from './utils';

type TabId = 'imagery' | 'data';

const ITEM_MARGIN_BOTTOM = 6;
const MINIMUM_AREA = 10;

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  mode: string;
  feature: I.ImmutableOf<ApiFeature>;
  blockedSources?: string[];
  /** Pass null if the user is not allowed to hide/unhide */
  setIsImageHidden:
    | null
    | ((
        f: I.ImmutableOf<ApiFeature>,
        orderableScene: I.ImmutableOf<ApiOrderableScene>,
        isHidden: boolean
      ) => void);

  openOrderImageryModal: (d: I.ImmutableOf<ApiOrderableScene>) => void;
  /**
   * Called with every listed scene whose card opens the modal, again whenever that list changes,
   * so the modal can page through the same rows the sidebar shows.
   */
  onPreviewScenesChange?: (scenes: readonly I.ImmutableOf<ApiOrderableScene>[]) => void;
  orderImageryState: OrderImageryState;
  setOrderImageryState: (s: OrderImageryState) => void;
  imageRefs: mapUtils.MapImageRefs;
  onChange: (imageRef: mapUtils.MapImageRef) => void;
  orderableScenesLoader: OrderableScenesLoader;
  getCurrentImageryContract: (
    opts: CachedApiGetterOptions
  ) => StatusMaybe<ApiImageryContract | null>;
}

export type DefaultProps = Pick<Props, 'blockedSources'>;

export interface OrderImageryState {
  showHiddenImagery: boolean;
  selectedSources: string[];
  sortAscending: boolean;
  showOrderedImagery: boolean;
  orderSubset: boolean;
  /**
   * The area to order. One drawn rectangle stays a Polygon the map can still resize; adding more
   * merges them into one geometry (a MultiPolygon when they don't touch) that is fixed until reset.
   */
  subsetFeature: geojson.Feature<geojson.Polygon | geojson.MultiPolygon> | null;
  /** The map's next rectangle is merged into `subsetFeature` instead of replacing it. */
  isAddingSubsetRectangle: boolean;
}

export const DEFAULT_ORDER_IMAGERY_STATE: OrderImageryState = {
  showHiddenImagery: false,
  selectedSources: [],
  sortAscending: false,
  showOrderedImagery: true,
  orderSubset: false,
  subsetFeature: null,
  isAddingSubsetRectangle: false,
};

const OrderImagerySidebar: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  organization,
  profile,
  feature,
  setIsImageHidden,
  openOrderImageryModal,
  onPreviewScenesChange,
  blockedSources = C.THUMBNAIL_BLOCKED_SOURCES,
  setOrderImageryState,
  orderImageryState,
  onChange,
  imageRefs,
  orderableScenesLoader,
  getCurrentImageryContract,
}) => {
  const {
    showHiddenImagery,
    showOrderedImagery,
    sortAscending,
    orderSubset,
    subsetFeature,
    isAddingSubsetRectangle,
    selectedSources,
  } = orderImageryState;

  const featureId: number = feature.get('id');
  const {findBySceneKey} = useCart();
  const cartEnabled = isCartEnabled(organization, profile);

  const [selectedTab, setSelectedTab] = React.useState<TabId>('imagery');
  const listRef = React.useRef<HTMLDivElement>(null);
  const orderableScenesStatusMaybe = orderableScenesLoader(feature.get('id'));

  const orderableScenes: I.List<I.ImmutableOf<ApiOrderableScene>> = React.useMemo(() => {
    if (orderableScenesStatusMaybe.value) {
      return orderableScenesStatusMaybe.value.toList();
    } else {
      return I.List([]);
    }
  }, [orderableScenesStatusMaybe.value]);

  const loadingProgress = orderableScenesLoader.getProgress?.(feature.get('id')) ?? null;

  const thumbnailOverrideMap = React.useMemo(() => {
    const map = new Map<string, I.ImmutableOf<ApiOrderableScene>>();
    orderableScenes.forEach((scene) => {
      if (!scene) return;
      const sourceId = scene.get('sourceId');
      if (!sourceId.includes('MULTISPECTRAL')) {
        map.set(orderableScenePairKey(scene), scene);
      }
    });
    return map;
  }, [orderableScenes]);

  const filteredOrderableScenes: I.ImmutableListOf<ApiOrderableScene> = React.useMemo(() => {
    let filteredOrderableScenes = orderableScenes
      .filter((f) => {
        const sourceId = f?.get('sourceId');

        // No selected sources is the same as all sources checked
        return selectedSources.length && sourceId ? selectedSources.includes(sourceId) : true;
      })
      .filter((scene) => isSceneInTab(scene, selectedTab))
      .toList();
    if (sortAscending) {
      filteredOrderableScenes = filteredOrderableScenes.reverse().toList();
    }
    return filteredOrderableScenes;
  }, [orderableScenes, selectedSources, sortAscending, selectedTab]);

  // Count scenes in each tab for display
  const tabCounts = React.useMemo(() => {
    const sourceFilteredScenes = orderableScenes.filter((f) => {
      const sourceId = f?.get('sourceId');
      return selectedSources.length && sourceId ? selectedSources.includes(sourceId) : true;
    });

    const imageryScenesCount = sourceFilteredScenes.filter((scene) =>
      isSceneInTab(scene, 'imagery')
    ).size;

    const dataScenesCount = sourceFilteredScenes.filter((scene) =>
      isSceneInTab(scene, 'data')
    ).size;

    return {imagery: imageryScenesCount, data: dataScenesCount};
  }, [orderableScenes, selectedSources]);

  const isProcessing =
    feature.getIn(['properties', 'status']) === FEATURE_WAITING_FOR_PROCESSING_STATUS;

  const featureGeometry: geojson.Polygon | geojson.MultiPolygon = React.useMemo(
    () => feature.get('geometry').toJS(),
    [feature]
  );

  // dividers: calculate which years we have scenes for
  const availableSceneYears = React.useMemo(
    () =>
      filteredOrderableScenes
        .map((scene) => moment(scene!.get('sensingTime')).year())
        .toList()
        .sort((yearA, yearB) => featureUtils.alphanumericSort(yearA, yearB)),
    [filteredOrderableScenes]
  );

  // create an array of years we want dividers for (current through oldest year)
  const dividerYears: number[] = React.useMemo(() => {
    const dividerYears: number[] = [];
    const oldestYear: number = availableSceneYears.first();
    const currentYear: number = moment().year();
    for (let i: number = currentYear; i >= oldestYear; i--) {
      dividerYears.push(i);
    }
    if (sortAscending) {
      dividerYears.reverse();
    }
    return dividerYears;
  }, [availableSceneYears, sortAscending]);
  let dividerYearsIndex = 0;

  const isLoadingScenes = orderableScenesStatusMaybe.status === 'unknown';

  const children: React.ReactElement[] = [];
  // Filled as the cards are built below and published once the loop is done.
  const previewScenes: I.ImmutableOf<ApiOrderableScene>[] = [];
  let hiddenImagesCount = 0;
  // create a list of children including scenes and year dividers
  filteredOrderableScenes.forEach((scene, i) => {
    const dateString = scene!.get('sensingTime');
    const sourceId = scene!.get('sourceId');

    const isActive =
      imageRefs[0].cursor === dateString && imageRefs[0].layerKey.startsWith(sourceId);

    const isComparedCursor =
      imageRefs.some(
        (r) =>
          r.cursor === dateString &&
          layerUtils.isLayerKeyHighResTruecolor(r.layerKey) &&
          r.layerKey.startsWith(sourceId)
      ) && !isActive;

    let rightCardAction: null | React.ReactElement = null;
    // Set by the branches below that offer Preview; the same rows make up the modal's paging list.
    let opensModal = false;

    const cartItem = cartEnabled
      ? findBySceneKey({featureId, sourceId, sensingTime: dateString})
      : undefined;

    const isOrdered = scene!.get('isOrdered');
    const isProcessed = scene!.get('orderProcessed');
    const isHidden = featureUtils.isOrderableSceneHidden(feature, scene!);
    const isPartial = isOrdered && !scene!.get('isFullyOrdered');

    // On the thumbnail rather than in the rail, so state stays visible on rows a partial-area
    // selection grays out — those suppress the right element entirely.
    const status = sceneStatus({isOrdered, isProcessed, isPartial, cartItem});
    const canOrder = scene!.get('canBeOrdered');
    const layerKey = getLayerKeyForScene(scene!);
    const canLoadSceneOnMap = canLoadOrderableSceneOnMap(scene!);
    const maybeUpdateImageRef = () => {
      const matchesDateAndSource = imageRefs.some(
        (r) => r.cursor === dateString && r.layerKey.startsWith(sourceId)
      );
      if (!matchesDateAndSource && isProcessed && canLoadSceneOnMap) {
        onChange({cursor: dateString, layerKey: layerKey});
      }
    };

    // check if we should be overriding the thumbnail for this scene and find the url if so
    const msSourceId = scene!.get('sourceId');
    const thumbnailOverrideScene = msSourceId.includes('MULTISPECTRAL')
      ? thumbnailOverrideMap.get(orderableScenePairKey(scene!))
      : undefined;
    const {url: thumbnailOverrideUrl} = thumbnailOverrideScene
      ? featureUtils.imageAndSourceDetailsFromScene(thumbnailOverrideScene)
      : {url: undefined};

    if (isHidden) {
      hiddenImagesCount = hiddenImagesCount + 1;
    }

    // Don't show imagery that is hidden or has already been ordered
    if ((!showHiddenImagery && isHidden) || (!showOrderedImagery && isProcessed)) {
      return null;
    } else if (!isOrdered) {
      rightCardAction = (
        <>
          {cartItem ? (
            <RemoveFromCartAction item={cartItem} />
          ) : (
            setIsImageHidden && (
              <HiddenImageAction
                feature={feature}
                orderableScene={scene!}
                isHidden={isHidden}
                setIsImageHidden={setIsImageHidden}
                alwaysVisible={showHiddenImagery}
              />
            )
          )}

          <B.Button
            className={cs.railButton}
            intent={B.Intent.PRIMARY}
            small
            text="Preview"
            onClick={() => openOrderImageryModal(scene!)}
          />
        </>
      );
      opensModal = true;
    } else if (isOrdered && isProcessed && isPartial && canOrder) {
      // "Partial" used to label this control, which was also the only way to open the modal on
      // these rows. The badge says it now, so it goes back to naming what it does.
      rightCardAction = (
        <>
          {cartItem && <RemoveFromCartAction item={cartItem} />}

          <B.Button
            className={cs.railButton}
            intent={B.Intent.PRIMARY}
            small
            text="Preview"
            onClick={() => {
              maybeUpdateImageRef();
              openOrderImageryModal(scene!);
            }}
          />
        </>
      );
      opensModal = true;
    } else if (isOrdered && isProcessed && !!layerKey) {
      // Nothing left to buy, but there is delivered imagery to look at, so the row's one action
      // puts it on the map. Gated on the layer key existing rather than on
      // canLoadOrderableSceneOnMap: that only accepts high-res truecolor, which would leave every
      // other delivered scene with no way to be viewed from here at all.
      rightCardAction = (
        <B.Button
          className={cs.railButton}
          outlined
          intent={B.Intent.PRIMARY}
          small
          text="View"
          onClick={() => onChange({cursor: dateString, layerKey})}
        />
      );
    }
    // Processing scenes keep an empty rail: there is nothing to buy and nothing delivered to look
    // at yet.

    // for our initial scene, always put a divider above it
    if (i === 0) {
      children.push(
        <div key={dividerYears[dividerYearsIndex]} className={cs.yearMarker}>
          <span>{dividerYears[dividerYearsIndex]}</span>
          <div className={cs.yearMarkerLine} />
        </div>
      );
    }
    // if we are trying to insert a scene and haven't reached a divider for it,
    // continue to insert dividers until we get to the one for that scene's year
    const date = moment(dateString);
    for (let n = 0; n < dividerYears.length; n++) {
      if (
        (date.year() > dividerYears[dividerYearsIndex] && sortAscending) ||
        (date.year() < dividerYears[dividerYearsIndex] && !sortAscending)
      ) {
        dividerYearsIndex++;
        children.push(
          <div key={dividerYears[dividerYearsIndex]} className={cs.yearMarker}>
            <span>{dividerYears[dividerYearsIndex]}</span>
            <div className={cs.yearMarkerLine} />
          </div>
        );
      }
    }

    let grayOut = false;
    if (orderSubset && subsetFeature) {
      // get intersection area of drawn rectangle and feature
      let featureIntersection: geojson.Feature<Polygon | MultiPolygon, any> | null = null;

      try {
        // TODO(fiona): Report out that this failed rather than it looking
        // like there’s no intersection area. Still, it’s better than lost in
        // space.
        featureIntersection = intersectGeometries(featureGeometry, subsetFeature.geometry);
      } catch (e) {
        Sentry.captureException(e, {
          extra: {
            featureId: feature.get('id'),
            subsetFeatureGeometry: JSON.stringify(subsetFeature.geometry),
          },
        });

        console.error(e);
      }

      // check if scene intersects with the drawn rectangle
      const sceneGeometry = scene!.get('sceneGeometry').toJS();
      const intersectionArea =
        featureIntersection &&
        getIntersectionInAcres(
          sceneGeometry as Polygon | MultiPolygon,
          featureIntersection.geometry as Polygon | MultiPolygon
        );

      const minumumAcresForSource: number | undefined =
        SOURCE_DETAILS_BY_ID[sourceId]?.minimum_order_acres;

      // Gray out scenes if the intersection between the scene, property,
      // and drawn rectangle is less than the required minimum order.
      grayOut =
        !intersectionArea ||
        intersectionArea < MINIMUM_AREA ||
        (!!minumumAcresForSource && intersectionArea < minumumAcresForSource);
    }

    // Nothing to act on while an order is still being processed. No tooltip: the thumbnail spells
    // "Processing" out on hover, which is the same gesture and says it in the row itself.
    const isAwaitingDelivery = isOrdered && !isProcessed;

    // A grayed-out row hides its rail, so its Preview isn't reachable either.
    if (opensModal && !grayOut) {
      previewScenes.push(scene!);
    }

    children.push(
      <B.Tooltip
        key={dateString + sourceId + '-tooltip'}
        disabled={!grayOut}
        content={
          grayOut
            ? 'Partial order not available for this scene. Your drawn area may be too small or outside the scene’s coverage.'
            : undefined
        }
      >
        <DateSelectCard
          key={dateString + sourceId}
          className={
            grayOut || isAwaitingDelivery
              ? cs.isDisabled
              : classnames(cs.rowHoverable, {
                  [cs.isActive]: isActive,
                  [cs.isComparedCursor]: isComparedCursor,
                })
          }
          style={{
            marginBottom: i === filteredOrderableScenes.size - 1 ? 0 : ITEM_MARGIN_BOTTOM,
          }}
          // No row-level click: everything the row can do is a button in its rail now, so a
          // click anywhere else shouldn't quietly do one of them.
          blockedSources={blockedSources}
          isOrdered={isProcessed}
          renderRightElement={
            grayOut
              ? () => null
              : () => (
                  <div className={cs.cardRightElement}>
                    <div className={cs.rightCardActions}>{rightCardAction}</div>
                  </div>
                )
          }
          orderableScene={scene!}
          organization={organization}
          layerKey={layerKey}
          thumbnailOverrideUrl={thumbnailOverrideUrl}
          thumbnailBadge={status ? <SceneStatusBadge status={status} /> : undefined}
          thumbnailOverlay={status ? <SceneStatusOverlay status={status} /> : undefined}
        />
      </B.Tooltip>
    );
  });

  // Keyed on the scenes' identities so a modal that is already open follows scenes still
  // streaming in and filter changes, without republishing on every render.
  const previewScenesSignature = previewScenes.map(orderableScenePairKey).join('|');
  const previewScenesRef = React.useRef(previewScenes);
  previewScenesRef.current = previewScenes;
  React.useEffect(() => {
    onPreviewScenesChange?.(previewScenesRef.current);
    return () => onPreviewScenesChange?.([]);
  }, [onPreviewScenesChange, previewScenesSignature]);

  const atLeastOneImageAvailable = !!children.length;
  if (!atLeastOneImageAvailable && !isLoadingScenes) {
    children.push(
      <div key="no-filter-imagery" style={{height: '100%'}}>
        <B.NonIdealState
          iconMuted={false}
          icon={isProcessing ? 'time' : 'media'}
          title={isProcessing ? 'Processing in progress' : 'No data matches this criteria'}
          description={
            isProcessing ? (
              <>
                In the meantime, explore additional data in the{' '}
                <a
                  href={`/${getOrgPrefix(organization)}/settings/layers`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Layers Library
                </a>
                .
              </>
            ) : (
              <>
                Please update your filters and try again, or consider adding more data from the{' '}
                <a
                  href={`/${getOrgPrefix(organization)}/settings/layers`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Layers Library
                </a>
                .
              </>
            )
          }
          action={
            isProcessing ? (
              <B.AnchorButton
                href={`/${getOrgPrefix(organization)}/settings/layers`}
                target="_blank"
                intent="primary"
              >
                Layers Library
              </B.AnchorButton>
            ) : (
              <></>
            )
          }
        />
      </div>
    );
  }

  function renderOrderSubsetPane() {
    const intersectionArea =
      featureGeometry &&
      subsetFeature &&
      getIntersectionInAcres(featureGeometry, subsetFeature.geometry);
    const areaAcres = intersectionArea ?? 0;

    const subsetAreaHeader = (
      <div className={cs.subsetAreaHeader}>
        <h3>{subsetFeature ? `Area in Property: ${areaAcres} Acres` : `Order Partial Area`}</h3>
        <div>
          <B.Tooltip content="Help">
            <B.AnchorButton
              icon="info-sign"
              minimal
              href="https://lens.tech/support/article/8-ordering-high-resolution-imagery#partial"
              target="_blank"
            />
          </B.Tooltip>
        </div>
      </div>
    );

    const subsetActions = subsetFeature && (
      <div className={cs.subsetActions}>
        <B.Button
          small
          icon="add"
          text="Add another rectangle"
          active={isAddingSubsetRectangle}
          onClick={() =>
            setOrderImageryState({
              ...orderImageryState,
              isAddingSubsetRectangle: !isAddingSubsetRectangle,
            })
          }
        />
        <B.Button
          small
          minimal
          icon="reset"
          text="Start over"
          onClick={() =>
            setOrderImageryState({
              ...orderImageryState,
              subsetFeature: null,
              isAddingSubsetRectangle: false,
            })
          }
        />
      </div>
    );

    let callout: React.ReactNode;
    if (!subsetFeature) {
      callout = (
        <B.Callout icon={null} intent={B.Intent.PRIMARY}>
          {subsetAreaHeader}
          <p>Draw a rectangle on the map to order data for that area.</p>
        </B.Callout>
      );
    } else if (isAddingSubsetRectangle) {
      callout = (
        <B.Callout icon={null} intent={B.Intent.PRIMARY}>
          {subsetAreaHeader}
          <p>
            Draw another rectangle on the map. It is added to your selection, and overlapping
            rectangles are merged.
          </p>
          {subsetActions}
        </B.Callout>
      );
    } else if (areaAcres < MINIMUM_AREA) {
      callout = (
        <B.Callout icon={null} intent={B.Intent.DANGER}>
          {subsetAreaHeader}
          <p>
            Your selection is less than the minimum area of {MINIMUM_AREA} acres within your
            property boundary for ordering partial data. Please draw a larger rectangle or add
            another.
          </p>
          {subsetActions}
        </B.Callout>
      );
    } else {
      callout = (
        <B.Callout icon={null} intent={B.Intent.SUCCESS}>
          {subsetAreaHeader}
          <p>Order for the selected area from the options below.</p>
          {subsetActions}
        </B.Callout>
      );
    }

    return <div className={cs.subsetPane}>{callout}</div>;
  }

  function renderSourceFilter(orderableScenes: I.ImmutableOf<ApiOrderableScene[]>) {
    // TODO(maya): In the future it would be great to have a list of all possible
    // options for an organization so this list does not change based on what is
    // currently available and ordered to generate options.

    // Filter to only show sources relevant to the current tab (data vs imagery)
    const tabFilteredScenes = orderableScenes.filter((scene) => isSceneInTab(scene, selectedTab));

    // Only show sources that have scenes in the current tab
    const tabSourceIds = tabFilteredScenes.map((f) => f?.get('sourceId')).toSet();

    // Also include any selected sources that don't have scenes in this property at all,
    // so users can uncheck them (avoids getting stuck with an empty filtered list)
    // This could happen in a property switch
    const allPropertySourceIds = orderableScenes.map((f) => f?.get('sourceId')).toSet();
    const orphanedSelectedSources = selectedSources.filter(
      (s) => !allPropertySourceIds.includes(s)
    );

    const sourceFilterOptions = tabSourceIds
      .concat(orphanedSelectedSources)
      .toSet()
      .sort((sourceA, sourceB) => featureUtils.alphanumericSort(sourceA, sourceB));

    const filterHighResolutionSourceIds = (sourceIds: string[]) =>
      sourceIds.filter((s) => {
        const sourceDetails = featureUtils.getSourceDetails(s as featureUtils.SourceDetailsId);
        return !!sourceDetails?.resolution && sourceDetails.resolution <= 1;
      });

    const highResSources = filterHighResolutionSourceIds(sourceFilterOptions.toJS());
    const isHighResFilterActive =
      selectedSources.length > 0 &&
      highResSources.length === selectedSources.length &&
      selectedSources.every((s) => highResSources.includes(s));

    return (
      <B.Popover
        className={cs.filterButton}
        interactionKind={B.PopoverInteractionKind.CLICK}
        minimal
        position={B.Position.BOTTOM_RIGHT}
        content={
          <B.Menu>
            <B.MenuItem
              icon={!sortAscending ? 'small-tick' : 'blank'}
              shouldDismissPopover={false}
              onClick={() => setOrderImageryState({...orderImageryState, sortAscending: false})}
              text="Newest to oldest"
            />
            <B.MenuItem
              icon={sortAscending ? 'small-tick' : 'blank'}
              shouldDismissPopover={false}
              onClick={() => setOrderImageryState({...orderImageryState, sortAscending: true})}
              text="Oldest to newest"
            />
            <B.MenuDivider />
            <B.MenuItem
              icon={selectedSources.length === 0 ? 'small-tick' : 'blank'}
              text={'All sources'}
              onClick={() =>
                setOrderImageryState({
                  ...orderImageryState,
                  selectedSources: [],
                })
              }
              shouldDismissPopover={false}
            />
            {sourceFilterOptions
              .map((sourceId) => {
                if (!sourceId) {
                  return;
                }
                const sourceDetails = featureUtils.getSourceDetails(
                  sourceId as featureUtils.SourceDetailsId
                );

                // Should never happen but if we can't find source details
                // for the id here we have nothing to show for this menu item
                if (!sourceDetails) {
                  return;
                }

                return (
                  <B.MenuItem
                    popoverProps={{
                      captureDismiss: true,
                      interactionKind: B.PopoverInteractionKind.CLICK,
                    }}
                    shouldDismissPopover={false}
                    key={sourceId}
                    text={`${sourceDetails.operator} ${sourceDetails.name}${sourceDetails.resolution != null ? ` (${parseFloat(sourceDetails.resolution.toFixed(3))}m)` : ''}`}
                    icon={selectedSources.includes(sourceId) ? 'small-tick' : 'blank'}
                    onClick={() =>
                      setOrderImageryState({
                        ...orderImageryState,
                        selectedSources: selectedSources.includes(sourceId)
                          ? selectedSources.filter((s) => s !== sourceId)
                          : [...selectedSources, sourceId],
                      })
                    }
                  />
                );
              })
              .toArray()}
            <B.MenuDivider />
            <B.MenuItem
              icon={showOrderedImagery ? 'small-tick' : 'blank'}
              text={'Show ordered scenes'}
              onClick={() =>
                setOrderImageryState({
                  ...orderImageryState,
                  showOrderedImagery: !showOrderedImagery,
                })
              }
              shouldDismissPopover={false}
            />
            {/** only show the menu item if there are high-res sources to filter to */}
            {highResSources.length > 0 && (
              <B.MenuItem
                icon={isHighResFilterActive ? 'small-tick' : 'blank'}
                shouldDismissPopover={false}
                onClick={() => {
                  setOrderImageryState({
                    ...orderImageryState,
                    selectedSources: isHighResFilterActive ? [] : highResSources,
                  });
                }}
                text={`≤1m resolution only`}
              />
            )}
            {hiddenImagesCount > 0 && (
              <B.MenuItem
                icon={showHiddenImagery ? 'small-tick' : 'blank'}
                shouldDismissPopover={false}
                onClick={() =>
                  setOrderImageryState({
                    ...orderImageryState,
                    showHiddenImagery: !showHiddenImagery,
                  })
                }
                text={`Show hidden scenes (${hiddenImagesCount})`}
              />
            )}
            <B.MenuDivider />
            <B.MenuItem
              icon="blank"
              text="Reset selection"
              shouldDismissPopover={false}
              onClick={() => {
                setOrderImageryState({
                  ...orderImageryState,
                  subsetFeature: null,
                  selectedSources: [],
                  showOrderedImagery: true,
                  showHiddenImagery: false,
                });
              }}
            />
          </B.Menu>
        }
      >
        <B.Tooltip content="Filter and sort scenes">
          <B.AnchorButton
            icon="filter"
            minimal
            active={!!selectedSources.length}
            text={selectedSources.length > 0 ? `${selectedSources.length}` : undefined}
          />
        </B.Tooltip>
      </B.Popover>
    );
  }

  /**
   *
   * @param hiddenImagesCount - number of hidden images, used to know whether to enable show/hide toggle
   * @param orderableScenes - orderable scenes, used here for deriving filters
   */
  function renderTools(orderableScenes: I.ImmutableOf<ApiOrderableScene[]>): React.ReactNode {
    // Hide the toolbar if there are no scenes to sort/filter/hide
    if (!orderableScenes.size) return;

    const areaInM2 = featureUtils.getFeatureAreaM2(feature);
    const acreage = featureUtils.featureM2ToArea(areaInM2);

    const paidImagery = featureUtils.getPaidImagery(feature);
    const scenes = paidImagery?.get('scenes');
    const sceneLimit = paidImagery?.get('limit') ?? 0;
    const scenesPurchased = scenes?.size ?? 0;

    // An org that pays at checkout orders without a contract. A feature's scene limit still holds
    // for it, and 0 means no limit, as the server reads it.
    const orgPaysAtCheckout = paysAtCheckout(organization);
    const imageryContract = getCurrentImageryContract({});
    const canBillOrder = orgPaysAtCheckout || !!imageryContract.value;
    const exceedsSceneLimit = sceneLimit !== 0 && scenesPurchased >= sceneLimit;

    const disableSubsetTool = exceedsSceneLimit || !canBillOrder || acreage <= MINIMUM_AREA;

    return (
      <>
        {renderSourceFilter(orderableScenes)}
        <B.Tooltip
          content={
            acreage <= MINIMUM_AREA
              ? `Cannot order partial areas for properties under ${MINIMUM_AREA} acres`
              : disableSubsetTool
                ? 'Cannot order partial areas for this contract type'
                : ''
          }
        >
          <B.AnchorButton
            icon={<PolygonIcon />}
            minimal
            intent={B.Intent.PRIMARY}
            text={'Order partial'}
            active={orderSubset}
            disabled={disableSubsetTool}
            onClick={() =>
              setOrderImageryState({
                ...orderImageryState,
                orderSubset: !orderSubset,
                // Leaving the tool discards the drawing: with the pane gone there is nothing left
                // that shows the area or could clear it.
                ...(orderSubset && {subsetFeature: null, isAddingSubsetRectangle: false}),
              })
            }
          />
        </B.Tooltip>
      </>
    );
  }

  const hasSceneData = orderableScenes.size > 0;

  if (
    orderableScenesStatusMaybe.status === 'error' ||
    (orderableScenesStatusMaybe.status === 'some' && !isProcessing && !orderableScenes.size)
  ) {
    return <B.NonIdealState iconMuted={false} icon="media" title="No imagery available to order" />;
  }

  if (isLoadingScenes && !hasSceneData) {
    return (
      <div className={classnames(cs.container)}>
        <div className={cs.cardsContainer}>
          <div className={cs.loadingContainer}>
            <B.Spinner value={loadingProgress ?? undefined} />
            <p>Fetching orderable scenes…</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={classnames(cs.container)}>
      <div className={cs.tabs}>
        <B.Tabs
          id="order-imagery-tabs"
          selectedTabId={selectedTab}
          onChange={(newTabId: TabId) => {
            setSelectedTab(newTabId);
            setOrderImageryState({...orderImageryState, selectedSources: []});
          }}
        >
          <B.Tab
            id="imagery"
            title={`Imagery (${tabCounts.imagery > 999 ? '999+' : tabCounts.imagery})`}
          />
          <B.Tab id="data" title={`Data (${tabCounts.data > 999 ? '999+' : tabCounts.data})`} />
        </B.Tabs>
        <div className={cs.tools}>{renderTools(orderableScenes)}</div>
      </div>
      {isProcessing && atLeastOneImageAvailable && renderImageryIsLoadingWarning()}
      {isLoadingScenes && (
        <div className={cs.loadingMore}>
          <B.Spinner size={B.SpinnerSize.SMALL} value={loadingProgress ?? undefined} />
          <span>Loading more scenes…</span>
        </div>
      )}
      {orderSubset && renderOrderSubsetPane()}
      <div ref={listRef} className={cs.cardsContainer}>
        <div className={cs.cards}>{children}</div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderImagerySidebar;

const Footer: React.FC = () => {
  return (
    <div className={cs.footer}>
      <div>
        Need older imagery? We can process the archive further back. Email us at{' '}
        <a href="mailto:lens@upstream.tech">lens@upstream.tech</a>.
      </div>
      <div>
        Not seeing exactly what you need? Learn more about{' '}
        <a
          href="https://lens.tech/support/article/72-tasking"
          target="_blank"
          rel="noopener noreferrer"
        >
          tasking here
        </a>
        .
      </div>
      <div>
        Overwhelmed by the options? Check out our{' '}
        <a
          href="https://lens.tech/support/article/136-choosing-imagery"
          target="_blank"
          rel="noopener noreferrer"
        >
          guide to choosing imagery
        </a>
        .
      </div>
    </div>
  );
};

function renderImageryIsLoadingWarning() {
  return (
    <B.Callout icon={null} intent={B.Intent.WARNING} style={{margin: 0}}>
      Imagery is still loading, additional images may be available later.
    </B.Callout>
  );
}
